import React, { useCallback, useEffect, useMemo, useState } from "react";
import GlobalDirectoryPaginationSection from "../../../../global/directory/GlobalDirectoryPaginationSection";
import GlobalFeedback from "../../../../global/GlobalFeedback";
import ScheduleDirectoryFilters from "./ScheduleDirectoryFilters";
import ScheduleTable from "./ScheduleTable";
import ViewScheduleModal from "../modals/ViewScheduleModal";
import { getScheduleColumnDefs } from "../../utils/scheduleColumnDefs";
import { SCHEDULE_PAGE_SIZE } from "../../utils/scheduleListConstants";
import {
  applyScheduleDirectoryFilters,
  buildScheduleExportRows,
  hasActiveScheduleDirectoryFilters,
  INITIAL_SCHEDULE_DIRECTORY_FILTERS,
} from "../../utils/scheduleDirectoryFilters";
import { useScheduleList } from "../../utils/useSchedule";
import { SCHEDULE_DATA_CHANGED_EVENT } from "../../utils/scheduleDataEvents";
import { exportSchedules } from "../../api/schedulesApi";

export default function ScheduleDirectorySection({
  isAdmin = false,
  listScope = "self",
  showPanelHead = false,
  permissions,
  onEdit,
  onDelete,
  onComplete,
  onCancel,
  reloadToken = 0,
  emptyBaseline = "No schedule records yet.",
  emptyFiltered = "No records available",
}) {
  const { allRows, loading, refresh } = useScheduleList({ listScope });

  useEffect(() => {
    if (reloadToken) refresh();
  }, [reloadToken, refresh]);

  useEffect(() => {
    const onDataChanged = () => refresh();
    window.addEventListener(SCHEDULE_DATA_CHANGED_EVENT, onDataChanged);
    return () => window.removeEventListener(SCHEDULE_DATA_CHANGED_EVENT, onDataChanged);
  }, [refresh]);

  const [filters, setFilters] = useState(() => ({ ...INITIAL_SCHEDULE_DIRECTORY_FILTERS }));
  const [columnVisibility, setColumnVisibility] = useState(null);
  const [exportError, setExportError] = useState("");
  const [viewScheduleId, setViewScheduleId] = useState(null);

  const filtered = useMemo(
    () => applyScheduleDirectoryFilters(allRows, filters),
    [allRows, filters],
  );

  const pageSize = SCHEDULE_PAGE_SIZE;
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const page = Math.min(filters.page || 1, totalPages);

  const rows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const columnDefs = getScheduleColumnDefs(isAdmin);
  const visibleColumns = useMemo(() => {
    if (!columnVisibility) return columnDefs;
    return columnDefs.filter((col) => {
      if (col.id === "actions") return true;
      return columnVisibility[col.id] !== false;
    });
  }, [columnDefs, columnVisibility]);

  const handleExportCsv = useCallback(async () => {
    setExportError("");
    try {
      let exportRows;
      if (isAdmin && permissions?.canExport) {
        const res = await exportSchedules();
        const data = Array.isArray(res?.data) ? res.data : filtered;
        exportRows = buildScheduleExportRows(
          applyScheduleDirectoryFilters(data, filters),
          visibleColumns,
        );
      } else {
        exportRows = buildScheduleExportRows(filtered, visibleColumns);
      }
      if (!exportRows.length) {
        setExportError("No schedules found for export");
        return;
      }
      const headers = Object.keys(exportRows[0]);
      const csv = [
        headers.join(","),
        ...exportRows.map((record) =>
          headers
            .map((key) => {
              const value = String(record[key] ?? "").replace(/"/g, '""');
              return `"${value}"`;
            })
            .join(","),
        ),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const today = new Date();
      const y = today.getFullYear();
      const m = String(today.getMonth() + 1).padStart(2, "0");
      const d = String(today.getDate()).padStart(2, "0");
      link.download = `schedules-${y}-${m}-${d}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err?.message || "Failed to export CSV");
    }
  }, [filtered, visibleColumns, isAdmin, permissions?.canExport, filters]);

  const hasFilters = hasActiveScheduleDirectoryFilters(filters);
  const emptyMessage = allRows.length === 0 && !hasFilters ? emptyBaseline : emptyFiltered;

  const directoryHint = isAdmin
    ? "All office schedule entries across the organization."
    : "Only schedules you created while logged in are listed here.";

  return (
    <>
      {showPanelHead ? (
        <div className="user-builder-panel__head">
          <h3 className="user-builder-panel__title">Schedule directory</h3>
          <p className="user-builder-panel__hint">{directoryHint}</p>
        </div>
      ) : null}
      <GlobalFeedback type="error" text={exportError} role="alert" />
      <ScheduleDirectoryFilters
        isAdmin={isAdmin}
        filters={filters}
        onFilterChange={setFilters}
        rows={rows}
        catalogRows={allRows}
        onColumnVisibilityChange={setColumnVisibility}
        onExportCsv={permissions?.canExport ? handleExportCsv : undefined}
      />
      <ScheduleTable
        rows={rows}
        loading={loading}
        isAdmin={isAdmin}
        permissions={permissions}
        page={page}
        pageSize={pageSize}
        total={total}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onView={(s) => setViewScheduleId(s.id)}
        onEdit={onEdit}
        onDelete={onDelete}
        onComplete={onComplete}
        onCancel={onCancel}
        emptyMessage={emptyMessage}
        columnVisibilityOverride={columnVisibility}
        showPaginationFooter={false}
      />
      <GlobalDirectoryPaginationSection
        page={page}
        totalPages={totalPages}
        total={total}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        label="Schedule directory pagination"
      />
      <ViewScheduleModal
        scheduleId={viewScheduleId}
        onClose={() => setViewScheduleId(null)}
      />
    </>
  );
}
